// Excel export — 5 sheets: BOM, Compute Summary, Storage Summary, Network Summary, Methodology Notes
// Uses SheetJS (xlsx). Triggers a browser download.

import * as XLSX from 'xlsx';
import type { Project, SizingResult, SizingConfig } from '../../types';

// ─── Public API ───────────────────────────────────────────────────────────────

export function exportToExcel(
  project: Project,
  result: SizingResult,
  scenarioName: string,
): void {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildBOMSheet(result), 'BOM');
  XLSX.utils.book_append_sheet(wb, buildComputeSheet(result), 'Compute Summary');
  XLSX.utils.book_append_sheet(wb, buildStorageSheet(result), 'Storage Summary');
  XLSX.utils.book_append_sheet(wb, buildNetworkSheet(result), 'Network Summary');
  XLSX.utils.book_append_sheet(wb, buildMethodologySheet(result, project, scenarioName), 'Methodology Notes');

  const safeClient = project.clientName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeScenario = scenarioName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `EEN_Sizing_${safeClient}_${safeScenario}_${dateStr}.xlsx`;

  XLSX.writeFile(wb, filename);
}

// ─── Sheet builders ───────────────────────────────────────────────────────────

function buildBOMSheet(result: SizingResult): XLSX.WorkSheet {
  const headers = [
    'Category',
    'Site',
    'Environment',
    'Tier',
    'Description',
    'Quantity',
    'Unit',
    'Spec Notes',
    'Methodology Note',
    'Unit Cost',
    'Currency',
  ];

  // Sort: Category → Tier → Environment
  const sortedBom = [...result.bom].sort((a, b) => {
    const catCmp = a.category.localeCompare(b.category);
    if (catCmp !== 0) return catCmp;
    const tierCmp = (a.tierName ?? '').localeCompare(b.tierName ?? '');
    if (tierCmp !== 0) return tierCmp;
    return a.environment.localeCompare(b.environment);
  });

  const rows = sortedBom.map(item => [
    item.category,
    item.site,
    item.environment,
    item.tierName ?? '',
    item.description,
    item.quantity,
    item.unit,
    item.specNotes,
    item.methodologyNote,
    item.unitCost ?? '',
    item.currency ?? '',
  ]);

  return sheetFromData([headers, ...rows]);
}

function buildComputeSheet(result: SizingResult): XLSX.WorkSheet {
  const headers = [
    'Site',
    'Environment',
    'Tier',
    'Raw vCPU Required',
    'Raw Memory GB Required',
    'Physical Cores Required',
    'Physical Memory GB Required',
    'Hosts Without HA',
    'Hosts With HA',
    'Host — Min CPU GHz',
    'Host — Physical Cores',
    'Host — Memory GB',
    'Ultra-Low Latency VMs?',
    'Latency Constraint Count',
    'Notes',
  ];

  const rows = result.compute.map(r => [
    r.site,
    r.environment,
    r.tierName ?? '',
    r.rawVcpuRequired,
    r.rawMemoryGbRequired,
    r.physicalCoresRequired,
    r.physicalMemoryGbRequired,
    r.hostCountWithoutHA,
    r.hostCountWithHA,
    r.recommendedHostSpec.minCpuGhz,
    r.recommendedHostSpec.physicalCores,
    r.recommendedHostSpec.memoryGb,
    r.hasUltraLowLatencyVms ? 'Yes' : 'No',
    r.latencyConstraintCount,
    r.notes.join(' | '),
  ]);

  return sheetFromData([headers, ...rows]);
}

function buildStorageSheet(result: SizingResult): XLSX.WorkSheet {
  const headers = [
    'Site',
    'Usable SAN GB',
    'Usable NAS GB',
    'Usable Local GB',
    'Raw SAN GB',
    'Raw NAS GB',
    'Raw Local GB',
    'Total SAN IOPS',
    'Effective IOPS Required',
    'Notes',
  ];

  const rows = result.storage.map(r => [
    r.site,
    r.usableSanGb,
    r.usableNasGb,
    r.usableLocalGb,
    r.rawSanGb,
    r.rawNasGb,
    r.rawLocalGb,
    r.totalSanIops,
    r.effectiveIopsRequired,
    r.notes.join(' | '),
  ]);

  return sheetFromData([headers, ...rows]);
}

function buildNetworkSheet(result: SizingResult): XLSX.WorkSheet {
  const headers = [
    'Site',
    'Total VM Bandwidth (Mbps)',
    'East-West Bandwidth (Mbps)',
    'North-South Bandwidth (Mbps)',
    'Recommended Uplink (Gbps)',
    'Recommended Access Port Count',
    'Required VLAN Count',
    'Notes',
  ];

  const rows = result.network.map(r => [
    r.site,
    r.totalVmBandwidthMbps,
    r.eastWestBandwidthMbps,
    r.northSouthBandwidthMbps,
    r.recommendedUplinkGbps,
    r.recommendedAccessPortCount,
    r.requiredVlanCount,
    r.notes.join(' | '),
  ]);

  return sheetFromData([headers, ...rows]);
}

function buildMethodologySheet(
  result: SizingResult,
  project: Project,
  scenarioName: string,
): XLSX.WorkSheet {
  const scenario = project.scenarios.find(s => s.id === result.scenarioId)
    ?? project.scenarios.find(s => s.isBaseline)
    ?? project.scenarios[0];

  const cfg: SizingConfig = scenario.sizingConfig;

  const rows: (string | number | boolean)[][] = [
    ['EEN VM Infrastructure Sizing Tool — Methodology Notes'],
    [],
    ['Project',          project.name],
    ['Client',           project.clientName],
    ['Scenario',         scenarioName],
    ['Generated At',     result.generatedAt],
    [],
    ['─── Sizing Parameters ───────────────────────────────────────'],
    [],
    ['vCPU Overcommit Ratio',         cfg.vcpuOvercommitRatio],
    ['Memory Overcommit Ratio',       cfg.memoryOvercommitRatio],
    ['Platform Overhead %',           cfg.platformOverheadPct],
    ['HA Model',                      cfg.haModel],
    ['Redundancy Model',              cfg.redundancyModel],
    ['DR Tier',                       cfg.drTier],
    ['DR Compute Fraction %',         cfg.drComputeFractionPct],
    ['Storage Architecture',          cfg.storageArchitecture],
    ['Storage Usable-to-Raw Ratio',   cfg.storageUsableToRawRatio],
    ['Storage Growth Buffer %',       cfg.storageGrowthBufferPct],
    ['RAID Write Penalty Factor',     cfg.raidWritePenaltyFactor],
    [],
    ['─── Methodology Notes by Domain ────────────────────────────'],
    [],
    ['COMPUTE'],
    ...result.compute.map(r => [
      `  ${r.site} / ${r.environment}${r.tierName ? ` / ${r.tierName}` : ''}`,
      r.notes.join(' | '),
    ]),
    [],
    ['STORAGE'],
    ...result.storage.map(r => [
      `  ${r.site}`,
      r.notes.join(' | '),
    ]),
    [],
    ['NETWORK'],
    ...result.network.map(r => [
      `  ${r.site}`,
      r.notes.join(' | '),
    ]),
    [],
    ['─── BOM Methodology Notes ───────────────────────────────────'],
    [],
    ['Category', 'Description', 'Methodology Note'],
    ...result.bom.map(item => [
      item.category,
      item.description,
      item.methodologyNote,
    ]),
  ];

  return sheetFromData(rows);
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function sheetFromData(rows: (string | number | boolean)[][]): XLSX.WorkSheet {
  return XLSX.utils.aoa_to_sheet(rows);
}
